import {
  faBed,
  faCalendar,
  faChevronCircleDown,
  faChevronCircleUp,
  faPlane,
  faTag,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'utils/shared/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserLastSearch } from 'services/user';
import { GetUserLastSearchResponse } from 'ts/shared/interfaces/User';
import withErrorBoundary from './withErrorBoundary';
import * as idb from 'utils/shared/idbUrl';

type SearchHistoryProduct = 'Hotels' | 'Flights' | 'Dynamic' | 'IL' | 'Transfers';
interface SearchHistoryProps {
  productOverride?: SearchHistoryProduct;
  hideHeader?: boolean;
  hideArrow?: boolean;
}

const SearchHistory: FC<SearchHistoryProps> = ({ hideArrow = false, productOverride, hideHeader = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [product, setProduct] = useState<SearchHistoryProduct>();
  const [data, setData] = useState<GetUserLastSearchResponse>();
  const [shortMode, setShortMode] = useState(false);

  useEffect(() => {
    const pathnameLower = pathname.toLowerCase();
    let product: SearchHistoryProduct | undefined = productOverride;
    if (!product) {
      if (pathnameLower.includes('/ilpackages')) {
        product = 'IL';
      } else if (search.toLocaleLowerCase().includes('=dynamic_fmhm')) {
        product = 'Dynamic';
      } else if (pathnameLower.includes('/transfers')) {
        product = 'Transfers';
      } else if (pathnameLower.includes('/flights')) {
        product = 'Flights';
      } else if (pathnameLower.includes('/hotels')) {
        product = 'Hotels';
      }
    }
    setProduct(product);

    if (product) {
      (async () => {
        const data = await getUserLastSearch(product);
        setData(data);
      })();
    }

    const shortMode = localStorage.getItem('historyShortMode');
    if (shortMode && !hideHeader) {
      setShortMode(shortMode === 'true');
    }
  }, [pathname, search, productOverride, hideHeader]);

  const getGuestsCount = (roomsString: string, isAdults: boolean = true) => {
    if (!roomsString) return -1;

    const adult = roomsString
      .split(';')
      .reduce((acc: number, room: string) => acc + +room.split('_')[0], 0);
    const child = roomsString
      .split(';')
      .reduce((acc: number, room: string) => acc + room.split('_').length - 1, 0);

    return isAdults ? adult : child;
  };

  const onClickRow = (item: any) => {
    const searchParamsStr = new URLSearchParams({
      ...item,
    }).toString();
    const cabineTypes = {
      eEconomyClass: '0',
      eEconomyPlusClass: '3',
      eBusinessClass: '1',
    };
    const searchTypes = {
      OneWay: '0',
      RoundTrip: '1',
      MultipleDestinations: '2',
    };

    if (product === 'Hotels') {
      idb.deletePageStateByPartialKey(pathname);
      navigate({
        pathname: (pathname || '').toLowerCase().replace(/\/results\/?$/, '') + '/results',
        search: searchParamsStr,
      });
    } else if (product === 'Flights') {
      idb.deletePageStateByPartialKey('flights');
      const cabineType = cabineTypes[item?.[0].cabineType as keyof typeof cabineTypes];
      const searchType = searchTypes[item?.[0].searchType as keyof typeof searchTypes];

      let arrivalCode = '';
      let arrivalDesc = '';
      let departureCode = '';
      let departureDesc = '';
      let departureDate = '';
      let arrivalDate = '';
      if (item?.[0].searchType === 'MultipleDestinations') {
        arrivalCode = item
          .reduce((acc: string[], curr: any) => [...acc, curr.arrivalCode], [])
          .join('|');
        arrivalDesc = item
          .reduce((acc: string[], curr: any) => [...acc, curr.arrivaleDesc || curr.arrivalCode], [])
          .join('|');
        departureCode = item
          .reduce((acc: string[], curr: any) => [...acc, curr.departureCode], [])
          .join('|');
        departureDesc = item
          .reduce(
            (acc: string[], curr: any) => [...acc, curr.departureDesc || curr.departureCode],
            []
          )
          .join('|');
        departureDate = item
          .reduce((acc: string[], curr: any) => [...acc, curr.departureDate], [])
          .join('|');
        arrivalDate = item
          .reduce((acc: string[], curr: any) => [...acc, curr.arrivalDate], [])
          .join('|');
      } else {
        arrivalCode = item?.[0].arrivalCode;
        arrivalDesc = item?.[0].arrivaleDesc;
        departureCode = item?.[0].departureCode;
        departureDesc = item?.[0].departureDesc;
        departureDate = item?.[0].departureDate;
        arrivalDate = item?.[0].arrivalDate;
      }

      let additionalFrom = '';
      let additionalTo = '';
      let additionalFromLabel = '';
      let additionalToLabel = '';
      if (item?.[0].searchType === 'RoundTrip') {
        additionalFrom = item[0].arrivalCode;
        additionalFromLabel = item[0].arrivalDesc || item[0].arrivalCode;
        additionalTo = item[0].departureCode;
        additionalToLabel = item[0].departureDesc || item[0].departureCode;
      }

      const flightSearchParams = new URLSearchParams({
        departureDesc,
        departureCode,
        arrivalDesc,
        arrivalCode,
        departureDate,
        arrivalDate: arrivalDate,
        numberOfAdults: item?.[0].numberOfAdults,
        numberOfChildren: item?.[0].numberOfChildren,
        numberOfYouths: item?.[0].numberOfYouths,
        numberOfSeniors: item?.[0].numberOfSeniors,
        numberOfInfants: item?.[0].numberOfInfants,
        cabineType,
        searchType,
        toValue: item?.[0].toValue,
        numberOfBags: item?.[0].numberOfBags,
        directFlight: item?.[0].directFlight,
        additionalFrom,
        additionalTo,
        additionalFromLabel,
        additionalToLabel,
      }).toString();

      navigate({ pathname: '/flights/results', search: flightSearchParams });
    } else if (product === 'IL') {
      idb.deletePageStateByPartialKey('ilpackages');

      const type = {
        eTOURS: '1',
        eNOFSHON: '2',
      };
      const params = new URLSearchParams({
        checkIn: item.checkIn,
        checkOut: item.checkOut,
        city: item.city,
        cityCode: item.cityCode + '-0',
        flightSourcesDefault: item.flightSourcesDefault,
        roomType: item.roomType,
        type: type[item.type as keyof typeof type],
        isSale: item.isSale,
      }).toString();
      navigate({ pathname: '/ilpackages/results', search: params });
    } else if (product === 'Transfers') {
      idb.deletePageStateByPartialKey('transfers');
      const types = {
        Address: '1',
        Airport: '0',
      };

      const newp = {
        ...item,
        'pickup.type': types[item.pickup.type as keyof typeof types],
        'dropoff.type': types[item.dropoff.type as keyof typeof types],
        'pickup.code': item.pickup.code,
        'pickup.desc': item.pickup.desc,
        'pickup.latitude': item.pickup.latitude,
        'pickup.longitude': item.pickup.longitude,
        'dropoff.code': item.dropoff.code,
        'dropoff.desc': item.dropoff.desc,
        'dropoff.latitude': item.dropoff.latitude,
        'dropoff.longitude': item.dropoff.longitude,
      };
      const params = new URLSearchParams(newp).toString();
      navigate({ pathname: '/transfers/results', search: params });
    } else if (product === 'Dynamic') {
      idb.deletePageStateByPartialKey('dynamic-fh');
      const cabineType = cabineTypes[item.cabineType as keyof typeof cabineTypes] || '0';
      const params = new URLSearchParams({
        ...item,
        cabineType,
        matrixConfig: item.matrixConfig.matrixConfig,
      });
      params.delete('cityArrivalCode');
      params.delete('searchOption');
      navigate({ pathname: '/dynamic-fh/details', search: params.toString() });
    }
  };

  const toggleShortMode = () => {
    const newShortMode = !shortMode;
    localStorage.setItem('historyShortMode', newShortMode.toString());
    setShortMode(newShortMode);
  };

  return (
    <Fragment>
      {product && (
        <div
          className={
            's-history-wrapper' +
            (shortMode && !hideHeader ? ' s-history-short' : '') +
            (hideHeader ? ' s-history-compact' : '')
          }
        >
          {!hideHeader &&
            !!(
              data?.hotelSearch.length ||
              data?.flightSearch.length ||
              data?.ilSearch.length ||
              data?.dynamicSearch.length ||
              data?.transferSearch.length
            ) && (
              <div className="s-history-header">
                {t('search.your_latest_searches')}{' '}
                {!hideArrow && (
                  <FontAwesomeIcon
                    icon={shortMode ? faChevronCircleUp : faChevronCircleDown}
                    className="cur-poi ph-10"
                    size="xs"
                    onClick={toggleShortMode}
                  />
                )}
              </div>
            )}

          {product === 'Hotels' && data?.hotelSearch && (
            <div className="s-history-content">
              {data.hotelSearch
                .sort(
                  (a, b) => new Date(b.historyDate).getTime() - new Date(a.historyDate).getTime()
                )
                .map((item, index) => (
                  <div className="s-history-row" key={index} onClick={() => onClickRow(item)}>
                    <div className="text-black text-bold mb-5">{item.city}</div>
                    <div className="mb-5 text-sm">
                      <FontAwesomeIcon icon={faCalendar} /> {dateFormat(item.checkIn, 'dd MMM')} -{' '}
                      {dateFormat(item.checkOut, 'dd MMM')}
                    </div>
                    <div className="text-sm">
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faBed} className="mr-3" />
                        {item.roomsString.split(';').length === 1
                          ? t('search.room')
                          : t('search.rooms')}
                        : {item.roomsString.split(';').length}
                      </span>
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faUser} className="mr-3" />
                        {getGuestsCount(item.roomsString) === 1
                          ? t('passenger.guest')
                          : t('passenger.guests')}
                        : {getGuestsCount(item.roomsString)}
                      </span>
                      {getGuestsCount(item.roomsString, false) > 0 && (
                        <span>
                          <FontAwesomeIcon icon={faUser} className="mr-3" />{' '}
                          {getGuestsCount(item.roomsString, false) === 1
                            ? t('search.child')
                            : t('search.children')}
                          : {getGuestsCount(item.roomsString, false)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
          {product === 'Flights' && data?.flightSearch && (
            <div className="s-history-content">
              {data.flightSearch
                .sort(
                  (a, b) =>
                    new Date(b?.[0]?.historyDate).getTime() -
                    new Date(a?.[0]?.historyDate).getTime()
                )
                .map((item, index) => (
                  <div className="s-history-row" key={index} onClick={() => onClickRow(item)}>
                    <div className="text-bold text-black mb-5">
                      {item?.[0].departureCode} - {item?.[0].arrivalCode}
                    </div>
                    <div className="mb-5 text-bold">
                      {dateFormat(item?.[0].departureDate, 'dd MMM')}

                      {item?.[0].searchType !== 'OneWay' && (
                        <span> - {dateFormat(item?.[0].arrivalDate, 'dd MMM')}</span>
                      )}
                    </div>
                    <div className="mt-5 text-sm">
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faUser} className="mr-3" />
                        {item?.[0].numberOfPersons === 1
                          ? t('passenger.passenger')
                          : t('passenger.s')}
                        : {item?.[0].numberOfPersons}
                      </span>
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faPlane} className="mr-3" />
                        {t(item?.[0].searchType)}
                      </span>

                      {!!item?.[0].cabineType && (
                        <span className="mr-10">
                          <FontAwesomeIcon icon={faTag} className="mr-3" />{' '}
                          {t(item?.[0].cabineType)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
          {product === 'IL' && data?.ilSearch && (
            <div className="s-history-content">
              {data.ilSearch
                .sort(
                  (a, b) => new Date(b.historyDate).getTime() - new Date(a.historyDate).getTime()
                )
                .map((item, index) => (
                  <div className="s-history-row" key={index} onClick={() => onClickRow(item)}>
                    <div className="text-bold text-black mb-5">{item.city}</div>
                    <div className="mb-5 text-bold">
                      <FontAwesomeIcon icon={faCalendar} className="mr-3" />{' '}
                      {dateFormat(item.checkIn, 'dd MMM')} - {dateFormat(item.checkOut, 'dd MMM')}
                    </div>
                    <div className="mt-5 text-sm">
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faUser} className="mr-3" />
                        {item.roomType.split(';')[0] === '1'
                          ? t('search.adult')
                          : t('search.adults')}
                        : {item.roomType.split(';')[0]}
                      </span>
                      {item.roomType.split(';')?.[1] !== '0' && (
                        <span className="mr-10">
                          <FontAwesomeIcon icon={faUser} className="mr-3" />
                          {item.roomType.split(';')[1] === '1'
                            ? t('search.child')
                            : t('search.children')}
                          : {item.roomType.split(';')[1]}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
          {product === 'Dynamic' && data?.dynamicSearch && (
            <div className="s-history-content">
              {data.dynamicSearch
                .sort(
                  (a, b) => new Date(b.historyDate).getTime() - new Date(a.historyDate).getTime()
                )
                .map((item, index) => (
                  <div className="s-history-row" key={index} onClick={() => onClickRow(item)}>
                    <div className=" text-bold text-black mb-5">
                      {!!item.googleAutocomplete && item.googleAutocomplete.formatted_address}
                      {!item.googleAutocomplete &&
                        `${item.departureCode ? item.departureCode + ' - ' : ''}${item.countryCode ? item.countryCode : ''
                        }`}
                    </div>
                    <div className="mb-5 text-bold">
                      <FontAwesomeIcon icon={faCalendar} className="mr-3" />
                      {dateFormat(item.departureDate, 'dd MMM')} -{' '}
                      {dateFormat(item.arrivalDate, 'dd MMM')}{' '}
                    </div>
                    <div className="mt-5 text-sm">
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faUser} className="mr-3" />
                        {item.numberOfAdults + item.numberOfSeniors === 1
                          ? t('search.adult')
                          : t('search.adults')}
                        : {item.numberOfAdults + item.numberOfSeniors}
                      </span>
                      {item.numberOfChildren + item.numberOfInfants > 0 && (
                        <span className="mr-10">
                          <FontAwesomeIcon icon={faUser} className="mr-3" />
                          {item.numberOfChildren + item.numberOfInfants === 1
                            ? t('search.child')
                            : t('search.children')}
                          : {item.numberOfChildren + item.numberOfInfants}
                        </span>
                      )}

                      {!!item.cabineType && (
                        <span className="mr-10">
                          <FontAwesomeIcon icon={faTag} className="mr-3" /> {t(item.cabineType)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
          {product === 'Transfers' && data?.transferSearch && (
            <div className="s-history-content">
              {data.transferSearch
                .sort(
                  (a, b) => new Date(b.historyDate).getTime() - new Date(a.historyDate).getTime()
                )
                .map((item, index) => (
                  <div
                    className="s-history-row text-xsm"
                    key={index}
                    onClick={() => onClickRow(item)}
                  >
                    <div className="text-bold text-black mb-5">
                      {item.pickup.desc} - {item.dropoff.desc}
                    </div>
                    <div className="mb-5 text-bold">
                      <FontAwesomeIcon icon={faCalendar} className="mr-3" />
                      {dateFormat(item.departureDate, 'dd MMM')} -{' '}
                      {dateFormat(item.returnDate, 'dd MMM')}{' '}
                    </div>
                    <div className="mt-5 text-sm">
                      <span className="mr-10">
                        <FontAwesomeIcon icon={faUser} className="mr-3" />
                        {item.people === 1 ? t('passenger.passenger') : t('passenger.s')}:{' '}
                        {item.people}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(SearchHistory, 'SearchHistory');
